import { useState } from 'react';
import { FileText, Upload, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import COUNTRIES from '@/data/countries';

const ResumeServicePage = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    current_education: '',
    work_experience: '',
    target_country: '',
    service_type: 'resume', // resume, motivation_letter, both
    additional_info: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/service-requests', {
        ...formData,
        service_category: 'resume_writing',
        created_at: new Date().toISOString(),
      });
      
      toast.success('درخواست شما با موفقیت ثبت شد. به زودی با شما تماس خواهیم گرفت.');
      
      // Reset form
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        current_education: '',
        work_experience: '',
        target_country: '',
        service_type: 'resume',
        additional_info: '',
      });
    } catch (error) {
      toast.error('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen py-12 ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
            isDark ? 'bg-[#D4AF37]/10' : 'bg-[#D4AF37]/20'
          }`}>
            <FileText className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h1 className={`text-4xl sm:text-5xl font-light mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            نگارش رزومه و انگیزه‌نامه
          </h1>
          <p className={`text-lg ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>
            رزومه و انگیزه‌نامه حرفه‌ای برای موفقیت در پذیرش تحصیلی یا شغلی
          </p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { icon: Check, title: 'نگارش حرفه‌ای', desc: 'نگارش با استانداردهای بین‌المللی' },
            { icon: Check, title: 'بررسی تخصصی', desc: 'بررسی و ویرایش توسط متخصصان' },
            { icon: Check, title: 'ویرایش نامحدود', desc: 'تا رسیدن به نتیجه مطلوب' },
          ].map((item) => (
            <div key={item.title} className={`border rounded-xl p-6 text-center ${
              isDark 
                ? 'glass-effect border-white/10' 
                : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <item.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <h3 className={`font-medium mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>{item.title}</h3>
              <p className={`text-sm ${
                isDark ? 'text-zinc-400' : 'text-gray-600'
              }`}>{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className={`border rounded-2xl p-8 ${
          isDark 
            ? 'glass-effect border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <h2 className={`text-2xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>فرم درخواست</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>نام و نام خانوادگی *</label>
                <input
                  type="text"
                  name="full_name"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>شماره تماس *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>ایمیل *</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>نوع خدمت *</label>
              <select
                name="service_type"
                required
                value={formData.service_type}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none font-medium ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="resume" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>فقط رزومه</option>
                <option value="motivation_letter" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>فقط انگیزه‌نامه</option>
                <option value="both" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>هر دو</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>سطح تحصیلات فعلی *</label>
              <input
                type="text"
                name="current_education"
                required
                placeholder="مثلاً: کارشناسی مهندسی کامپیوتر"
                value={formData.current_education}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>سابقه کاری</label>
              <textarea
                name="work_experience"
                rows="3"
                placeholder="سابقه کاری خود را به طور خلاصه بنویسید..."
                value={formData.work_experience}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>کشور موردنظر *</label>
              <select
                name="target_country"
                required
                value={formData.target_country}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none font-medium ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>انتخاب کنید</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country} className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>توضیحات تکمیلی</label>
              <textarea
                name="additional_info"
                rows="4"
                placeholder="اطلاعات بیشتری که می‌خواهید با ما در میان بگذارید..."
                value={formData.additional_info}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black font-medium py-4 rounded-lg hover:shadow-lg hover:shadow-[#D4AF37]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'در حال ارسال...' : 'ارسال درخواست'}
            </button>
          </form>
        </div>

        {/* Additional Info */}
        <div className={`mt-8 border rounded-xl p-6 ${
          isDark 
            ? 'glass-effect border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <h3 className={`font-medium mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>چرا رزومه و انگیزه‌نامه حرفه‌ای؟</h3>
          <ul className={`space-y-3 ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span>افزایش شانس پذیرش تا ۷۰٪</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span>تطابق کامل با استانداردهای بین‌المللی</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span>نگارش توسط مشاوران با تجربه بین‌المللی</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span>پشتیبانی و ویرایش رایگان تا رضایت کامل</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResumeServicePage;
